import { JSONPath } from 'jsonpath-plus'
import _ from 'lodash'
import * as yup from 'yup'
/**
 * Stord Address Schema
 *
 * Also used as action step input schema for address inputs
 */
export const stordAddressSchema = yup.object({
  name: yup.string().notRequired(),
  line1: yup.string().notRequired(),
  line2: yup.string().notRequired(),
  company: yup.string().notRequired(),
  locality: yup.string().notRequired(),
  country_subdivision_code: yup.string().notRequired(),
  country_code: yup.string().notRequired(),
  postal_code: yup.string().notRequired(),
})

/**
 * Stord Customer Info Schema
 *
 * Also used as action step input schema for customer info inputs
 */
export const stordCustomerInfoSchema = yup.object({
  name: yup.string().notRequired(),
  email: yup.string().notRequired(),
  contact_phone: yup.string().notRequired(),
  address: stordAddressSchema.notRequired(),
})

/**
 * Stord Order Line Schema
 *
 * Also used as action step input schema for order line inputs
 */
export const stordOrderLineSchema = yup.object({
  sku: yup.string().notRequired(),
  unit: yup.string().notRequired(),
  quantity: yup.string().notRequired(),
  lot_number: yup.string().notRequired(),
  expires_at: yup.string().notRequired(),
  handling_unit: yup.string().notRequired(),
  cost_to_ship: yup.string().notRequired(),
  sale_price: yup.string().notRequired(),
  custom_order_line_reference: yup.string().notRequired(),
  customs_value: yup.string().notRequired(),
  sale_cost_excluding_tax: yup.string().notRequired(),
  currency_for_sale_cost: yup.string().notRequired(),
  customs_description: yup.string().notRequired(),
  country_code: yup.string().notRequired(),
  tariff_code: yup.string().notRequired(),
  integration_metadata: yup.object().notRequired(),
})

/**
 * Stord Order Document Schema
 */
export const stordOrderDocumentSchema = yup.object({
  order_id: yup.string().notRequired(),
  order_document_id: yup.string().notRequired(),
  document_id: yup.string().notRequired(),
  file_name: yup.string().notRequired(),
  document_type: yup.string().notRequired(),
})

/**
 * Stord Shipment Protection Schema
 */
export const stordShipmentProtectionSchema = yup.object({
  token: yup.string().notRequired(),
  storefront_id: yup.string().notRequired(),
})

// SALES ORDER -----------------------------------------------------------------------------------------

/**
 * Stord Create Sales Order Action Step Input Schema
 *
 * Used for validating input data in write integration action steps
 */
export const stordCreateSalesOrderActionStepInputSchema = yup.object({
  integration_metadata: yup.object().notRequired(),
  ship_to_customer: stordCustomerInfoSchema.notRequired(),
  sold_to_customer: stordCustomerInfoSchema.notRequired(),
  bill_to_customer: stordCustomerInfoSchema.notRequired(),
  shipment_protection: stordShipmentProtectionSchema.notRequired(),
  edi_metadata: yup.object().notRequired(),
  custom_reference: yup.string().notRequired(),
  order_number: yup.string().notRequired(),
  order_lines: yup.array().of(stordOrderLineSchema).notRequired(),
  priority: yup.number().oneOf([1, 2, 3, 4]).notRequired(),
  price: yup.number().notRequired(),
  cost_to_ship: yup.number().notRequired(),
  charged_shipping_cost: yup.number().notRequired(),
  notes: yup.string().notRequired(),
  gift_message: yup.string().notRequired(),
  expected_shipped_at: yup.string().notRequired(),
  expected_received_at: yup.string().notRequired(),
  tags: yup.array().of(yup.string()).notRequired(),
  channel_id: yup.string().notRequired(),
  external_posted_at: yup.string().notRequired(),
  activity_custom_reference: yup.string().notRequired(),
  order_documents: yup.array().of(stordOrderDocumentSchema).notRequired(),
  carrier_name: yup.string().notRequired(),
  carrier_scac: yup.string().notRequired(),
  carrier_service_method: yup.string().notRequired(),
  carrier_ship_option: yup.string().notRequired(),
  shipping_code: yup.string().notRequired(),
  print_option_1: yup.string().notRequired(),
  print_option_2: yup.string().notRequired(),
  print_option_3: yup.string().notRequired(),
  desired_delivery_date: yup.string().notRequired(),
  do_not_ship_before: yup.string().notRequired(),
  must_ship_by: yup.string().notRequired(),
  scheduled_pick_up: yup.string().notRequired(),
  preferred_facilities: yup.array().of(yup.string()).notRequired(),
  incoterm: yup.string().notRequired(),
  retail_po_number: yup.string().notRequired(),
  tax_identification_number: yup.string().notRequired(),
})

export type StordCreateSalesOrderActionStepInput = yup.InferType<
  typeof stordCreateSalesOrderActionStepInputSchema
>

// PURCHASE ORDER -----------------------------------------------------------------------------------------

/**
 * Stord Purchase Order Line Schema
 *
 * Also used as action step input schema for purchase order line inputs
 */
export const stordPurchaseOrderLineSchema = yup.object({
  sku: yup.string().notRequired(),
  unit: yup.string().notRequired(),
  quantity: yup.string().notRequired(),
  lot_number: yup.string().notRequired(),
  purchase_price: yup.string().notRequired(),
  cost_to_ship: yup.string().notRequired(),
  custom_order_line_reference: yup.string().notRequired(),
  integration_metadata: yup.object().notRequired(),
})

/**
 * Stord Create Purchase Order Action Step Input Schema
 *
 * Used for validating input data in write integration action steps
 */
export const stordCreatePurchaseOrderActionStepInputSchema = yup.object({
  integration_metadata: yup.object().notRequired(),
  supplier_id: yup.string().notRequired(),
  custom_reference: yup.string().notRequired(),
  priority: yup.number().oneOf([1, 2, 3, 4]).notRequired(),
  price: yup.number().notRequired(),
  cost_to_ship: yup.number().notRequired(),
  charged_shipping_cost: yup.number().notRequired(),
  notes: yup.string().notRequired(),
  expected_received_at: yup.string().notRequired(),
  tags: yup.array().of(yup.string()).notRequired(),
  order_number: yup.string().notRequired(),
  external_posted_at: yup.string().notRequired(),
  activity_custom_reference: yup.string().notRequired(),
  order_documents: yup.array().of(stordOrderDocumentSchema).notRequired(),
  external_order_tags: yup.array().of(yup.string()).notRequired(),
  preferred_facilities: yup.array().of(yup.string()).notRequired(),
  order_lines: yup.array().of(stordPurchaseOrderLineSchema).notRequired(),
})

export type StordCreatePurchaseOrderActionStepInput = yup.InferType<
  typeof stordCreatePurchaseOrderActionStepInputSchema
>

const stordSerialIdentifierSchema = yup.object({
  imei: yup.number().notRequired(),
  serial_number: yup.number().notRequired(),
  sim: yup.number().notRequired(),
  unit_id_1: yup.string().notRequired(),
  unit_id_2: yup.string().notRequired(),
  unit_id_3: yup.string().notRequired(),
})

const stordShipmentConfirmationLineItemSchema = yup.object({
  bundle_sku: yup.string().notRequired(),
  custom_order_line_reference: yup.string().notRequired(),
  expires_at: yup.string().notRequired(),
  handling_unit: yup.string().notRequired(),
  integration_metadata: yup.object().notRequired(),
  item_id: yup.string().notRequired(),
  listing: yup.string().notRequired(),
  lot_number: yup.string().notRequired(),
  order_line_id: yup.string().notRequired(),
  quantity: yup.number().notRequired(),
  serial_identifiers: yup.array(stordSerialIdentifierSchema).notRequired(),
  shipment_confirmation_line_item_id: yup.string().notRequired(),
  sku: yup.string().notRequired(),
  unit_of_measure: yup.string().notRequired(),
})

const stordShipmentConfirmationPackageLineItemSchema = yup.object({
  quantity: yup.number().notRequired(),
  sku: yup.string().notRequired(),
})

const stordShipmentConfirmationPackageSchema = yup.object({
  carrier_name: yup.string().notRequired(),
  carrier_scac: yup.string().notRequired(),
  carrier_service_method: yup.string().notRequired(),
  carrier_ship_option: yup.string().notRequired(),
  package_dims_unit: yup.string().notRequired(),
  package_height: yup.number().notRequired(),
  package_length: yup.number().notRequired(),
  package_number: yup.number().notRequired(),
  package_weight: yup.number().notRequired(),
  package_weight_unit: yup.string().notRequired(),
  package_width: yup.number().notRequired(),
  shipment_confirmation_package_line_items: yup
    .array(stordShipmentConfirmationPackageLineItemSchema)
    .notRequired(),
  shipped_at: yup.string().notRequired(),
  tracking_link: yup.string().notRequired(),
  tracking_number: yup.string().notRequired(),
})

const stordCarrierSchema = yup.object({
  name: yup.string().notRequired(),
  scac: yup.string().notRequired(),
  service_method: yup.string().notRequired(),
  ship_option: yup.string().notRequired(),
})

const stordDestinationAddressSchema = yup.object({
  address_type: yup.string().notRequired(),
  company: yup.string().notRequired(),
  country_code: yup.string().notRequired(),
  country_subdivision_code: yup.string().notRequired(),
  latitude: yup.number().notRequired(),
  line1: yup.string().notRequired(),
  line2: yup.string().notRequired(),
  line3: yup.string().notRequired(),
  locality: yup.string().notRequired(),
  longitude: yup.number().notRequired(),
  name: yup.string().notRequired(),
  normalized_address: yup.string().notRequired(),
  normalized_country_code: yup.string().notRequired(),
  normalized_country_subdivision_code: yup.string().notRequired(),
  normalized_line1: yup.string().notRequired(),
  normalized_line2: yup.string().notRequired(),
  normalized_locality: yup.string().notRequired(),
  normalized_name: yup.string().notRequired(),
  normalized_postal_code: yup.string().notRequired(),
  postal_code: yup.string().notRequired(),
  verification_status: yup.string().notRequired(),
})

const stordOrderSchema = yup.object({
  channel_id: yup.string().notRequired(),
  channel_name: yup.string().notRequired(),
  custom_reference: yup.string().notRequired(),
  external_posted_at: yup.string().notRequired(),
  inserted_at: yup.string().notRequired(),
  inserted_from: yup.string().notRequired(),
  integration_metadata: yup.object().notRequired(),
  network_id: yup.string().notRequired(),
  order_id: yup.string().notRequired(),
  order_number: yup.string().notRequired(),
  type: yup.string().notRequired(),
})

const stordOriginFacilitySchema = yup.object({
  alias: yup.string().notRequired(),
  facility_id: yup.string().notRequired(),
  name: yup.string().notRequired(),
})

const stordShipmentConfirmationDataSchema = yup.object({
  activity_custom_reference: yup.string().notRequired(),
  activity_number: yup.string().notRequired(),
  bol: yup.string().notRequired(),
  cancellation_id: yup.string().notRequired(),
  carrier: stordCarrierSchema.notRequired(),
  confirmation_number: yup.string().notRequired(),
  destination_address: stordDestinationAddressSchema.notRequired(),
  facility_id: yup.string().notRequired(),
  inserted_at: yup.string().notRequired(),
  is_canceled: yup.boolean().notRequired(),
  network_id: yup.string().notRequired(),
  notes: yup.string().notRequired(),
  order: stordOrderSchema.notRequired(),
  origin_facility: stordOriginFacilitySchema.notRequired(),
  package_dims_unit: yup.string().notRequired(),
  package_height: yup.number().notRequired(),
  package_length: yup.number().notRequired(),
  package_weight: yup.number().notRequired(),
  package_weight_unit: yup.string().notRequired(),
  package_width: yup.number().notRequired(),
  pro_number: yup.string().notRequired(),
  processed_at: yup.string().notRequired(),
  shipment_confirmation_id: yup.string().notRequired(),
  shipment_confirmation_line_items: yup
    .array(stordShipmentConfirmationLineItemSchema)
    .notRequired(),
  shipment_confirmation_packages: yup.array(stordShipmentConfirmationPackageSchema).notRequired(),
  shipped_at: yup.string().notRequired(),
  tracking_link: yup.string().notRequired(),
  tracking_number: yup.string().notRequired(),
  updated_at: yup.string().notRequired(),
  wms_posted_at: yup.string().notRequired(),
})

const stordMetaSchema = yup.object({
  hydration: yup
    .object({
      url: yup.string().notRequired(),
    })
    .notRequired(),
  occurred_at: yup.string().notRequired(),
  version: yup.number().notRequired(),
})

export const stordShipmentConfirmationWebhookSchema = yup.object({
  data: stordShipmentConfirmationDataSchema.required(),
  meta: stordMetaSchema.notRequired(),
  type: yup.string().required(),
})

// RECEIPT CONFIRMATION SCHEMAS -----------------------------------------------------------------------------------------

const stordReceiptConfirmationLineItemSchema = yup.object({
  custom_order_line_reference: yup.string().notRequired(),
  damages: yup.number().notRequired(),
  expires_at: yup.string().notRequired(),
  handling_unit: yup.string().notRequired(),
  integration_metadata: yup.object().notRequired(),
  item_id: yup.string().notRequired(),
  lot_number: yup.string().notRequired(),
  order_line_id: yup.string().notRequired(),
  quantity: yup.number().notRequired(),
  sku: yup.string().notRequired(),
  unit_of_measure: yup.string().notRequired(),
})

const stordReceiptConfirmationDataSchema = yup.object({
  activity_custom_reference: yup.string().notRequired(),
  activity_number: yup.string().notRequired(),
  bol: yup.string().notRequired(),
  cancellation_id: yup.string().notRequired(),
  confirmation_number: yup.string().notRequired(),
  facility_id: yup.string().notRequired(),
  inserted_at: yup.string().notRequired(),
  is_canceled: yup.boolean().notRequired(),
  network_id: yup.string().notRequired(),
  notes: yup.string().notRequired(),
  order: stordOrderSchema.notRequired(),
  origin_address: stordDestinationAddressSchema.notRequired(), // Reusing the same address schema
  processed_at: yup.string().notRequired(),
  receipt_confirmation_id: yup.string().notRequired(),
  receipt_confirmation_line_items: yup.array(stordReceiptConfirmationLineItemSchema).notRequired(),
  received_at: yup.string().notRequired(),
  updated_at: yup.string().notRequired(),
  wms_posted_at: yup.string().notRequired(),
})

export const stordReceiptConfirmationWebhookSchema = yup.object({
  data: stordReceiptConfirmationDataSchema.required(),
  meta: stordMetaSchema.notRequired(),
  type: yup.string().required(),
})

export const stordWebhookQuerySchema = yup.object({
  organizationId: yup.string().required(),
})

/**
 * Stord `v1.order.updated` webhook: each entry describes a field that changed.
 *
 * @see https://developers.stord.com/docs/webhooks
 */
export const stordOrderUpdateChangeItemSchema = yup.object({
  type: yup.string().required(),
  from: yup.mixed().notRequired(),
  to: yup.mixed().notRequired(),
})

export const stordOrderUpdateWebhookSchema = yup.object({
  meta: stordMetaSchema.notRequired(),
  type: yup.string().required(),
  data: yup
    .object({
      type: yup.string().notRequired(),
      order_number: yup.string().notRequired(),
      order_id: yup.string().notRequired(),
      custom_reference: yup.string().notRequired(),
      network_id: yup.string().notRequired(),
      tenant_id: yup.string().notRequired(),
      channel: yup
        .object()
        .shape({
          channel_id: yup.string().notRequired(),
          category: yup.string().notRequired(),
          display_name: yup.string().notRequired(),
        })
        .notRequired(),
      changes: yup.array().of(stordOrderUpdateChangeItemSchema).notRequired(),
    })
    .required(),
})

// TRANSFORMATION UTILITIES -----------------------------------------------------------------------------------------

/** A function that transforms a single scalar value. */
export type TransformationFn = (value: unknown) => unknown

/** Map of JSONPath → transformation function */
export type TransformationMapping = Record<string, TransformationFn>

/**
 * Type-safe parseFloat wrapper
 */
export const safeParseFloat = (value: unknown): number | null => {
  if (typeof value === 'number') {
    return value
  }
  if (typeof value === 'string') {
    const parsed = parseFloat(value)
    return isNaN(parsed) ? null : parsed
  }
  return null
}

/**
 * Type-safe parseInt wrapper
 */
export const safeParseInt = (value: unknown): number | null => {
  if (typeof value === 'number') {
    return Number.isInteger(value) ? value : null
  }
  if (typeof value === 'string') {
    const parsed = parseInt(value, 10)
    return isNaN(parsed) ? null : parsed
  }
  return null
}

/** Type-safe parseBoolean wrapper */
export const safeParseBoolean = (value: unknown): boolean | null => {
  if (typeof value === 'boolean') {
    return value
  }
  if (typeof value === 'string') {
    const lower = value.toLowerCase()
    if (lower === 'true' || lower === '1' || lower === 'yes') {
      return true
    }
    if (lower === 'false' || lower === '0' || lower === 'no') {
      return false
    }
  }
  if (typeof value === 'number') {
    return value !== 0
  }
  return null
}

/**
 * Transformations to convert string fields to numeric values in Stord receipt confirmation webhook data.
 */
export const STORD_RECEIPT_CONFIRMATION_TRANSFORMATIONS: TransformationMapping = {
  // Origin address fields
  '$.data.origin_address.latitude': safeParseFloat,
  '$.data.origin_address.longitude': safeParseFloat,

  // Receipt confirmation line item fields
  '$.data.receipt_confirmation_line_items[*].quantity': safeParseFloat,
  '$.data.receipt_confirmation_line_items[*].damages': safeParseFloat,
}

/**
 * Transformations to convert string fields to numeric values in the Stord get receipt confirmations
 * READ response. Unlike the webhook (a single `$.data` object), the read response is a list, so the
 * line items live under `$.data[*]`.
 */
export const STORD_RECEIPT_CONFIRMATIONS_READ_TRANSFORMATIONS: TransformationMapping = {
  '$.data[*].receipt_confirmation_line_items[*].quantity': safeParseFloat,
  '$.data[*].receipt_confirmation_line_items[*].damages': safeParseFloat,
}

/**
 * Transformations to convert string fields to numeric values in Stord shipment confirmation webhook data.
 */
export const STORD_SHIPMENT_CONFIRMATION_TRANSFORMATIONS: TransformationMapping = {
  // Line item quantity fields
  '$.data.shipment_confirmation_line_items[*].quantity': safeParseFloat,

  // Serial identifier fields
  '$.data.shipment_confirmation_line_items[*].serial_identifiers[*].serial_number': safeParseFloat,
  '$.data.shipment_confirmation_line_items[*].serial_identifiers[*].sim': safeParseFloat,
  '$.data.shipment_confirmation_line_items[*].serial_identifiers[*].imei': safeParseFloat,

  // Package dimension and weight fields at root level
  '$.data.package_height': safeParseFloat,
  '$.data.package_length': safeParseFloat,
  '$.data.package_weight': safeParseFloat,
  '$.data.package_width': safeParseFloat,

  // Package dimension and weight fields in packages array
  '$.data.shipment_confirmation_packages[*].package_height': safeParseFloat,
  '$.data.shipment_confirmation_packages[*].package_length': safeParseFloat,
  '$.data.shipment_confirmation_packages[*].package_weight': safeParseFloat,
  '$.data.shipment_confirmation_packages[*].package_width': safeParseFloat,

  // Package line item quantity fields
  '$.data.shipment_confirmation_packages[*].shipment_confirmation_package_line_items[*].quantity':
    safeParseFloat,

  // Destination address fields
  '$.data.destination_address.latitude': safeParseFloat,
  '$.data.destination_address.longitude': safeParseFloat,
}

/**
 * Transformations for the Get Shipment Confirmations READ response.
 * Unlike the webhook (a single `$.data` object), the read response is a list, so numeric
 * quantity / package fields live under `$.data[*]`. Serial identifiers stay strings — REST
 * documents them as strings, and parseFloat would drop alphanumeric values.
 */
export const STORD_SHIPMENT_CONFIRMATIONS_READ_TRANSFORMATIONS: TransformationMapping = {
  '$.data[*].shipment_confirmation_line_items[*].quantity': safeParseFloat,
  '$.data[*].package_height': safeParseFloat,
  '$.data[*].package_length': safeParseFloat,
  '$.data[*].package_weight': safeParseFloat,
  '$.data[*].package_width': safeParseFloat,
  '$.data[*].shipment_confirmation_packages[*].package_height': safeParseFloat,
  '$.data[*].shipment_confirmation_packages[*].package_length': safeParseFloat,
  '$.data[*].shipment_confirmation_packages[*].package_weight': safeParseFloat,
  '$.data[*].shipment_confirmation_packages[*].package_width': safeParseFloat,
  '$.data[*].shipment_confirmation_packages[*].shipment_confirmation_package_line_items[*].quantity':
    safeParseFloat,
}

/**
 * Transformations for the Get Delivery Tracking V2 READ response.
 * Line-item `actual` / `expected` / `quantity` arrive as strings (e.g. `"1.00000"`).
 */
export const STORD_DELIVERY_TRACKING_READ_TRANSFORMATIONS: TransformationMapping = {
  '$.data[*].order_shipments[*].line_items[*].actual': safeParseFloat,
  '$.data[*].order_shipments[*].line_items[*].expected': safeParseFloat,
  '$.data[*].order_shipments[*].line_items[*].quantity': safeParseFloat,
}

/**
 * Transformations to convert string inventory quantity fields in Stord API responses to numeric values.
 */
export const STORD_INVENTORY_TRANSFORMATIONS: TransformationMapping = {
  // Inventory quantity fields
  '$.data[*].total_on_hand': safeParseFloat,
  '$.data[*].unlocked': safeParseFloat,
  '$.data[*].receiving': safeParseFloat,
  '$.data[*].quarantined': safeParseFloat,
  '$.data[*].other': safeParseFloat,
  '$.data[*].locked': safeParseFloat,
  '$.data[*].damaged': safeParseFloat,
  // Network inventory report (nested under network_balances)
  '$.data[*].network_balances.accepted': safeParseFloat,
  '$.data[*].network_balances.allocated': safeParseFloat,
  '$.data[*].network_balances.available': safeParseFloat,
  '$.data[*].network_balances.actually_available': safeParseFloat,
  '$.data[*].network_balances.backordered': safeParseFloat,
  '$.data[*].network_balances.backordered_available': safeParseFloat,
  '$.data[*].network_balances.committed': safeParseFloat,
  '$.data[*].network_balances.incoming': safeParseFloat,
  '$.data[*].network_balances.locked': safeParseFloat,
  '$.data[*].network_balances.damaged': safeParseFloat,
  '$.data[*].network_balances.receiving': safeParseFloat,
  '$.data[*].network_balances.quarantined': safeParseFloat,
  '$.data[*].network_balances.other': safeParseFloat,
  '$.data[*].network_balances.on_hold': safeParseFloat,
  '$.data[*].network_balances.unlocked': safeParseFloat,
  '$.data[*].network_balances.total_on_hand': safeParseFloat,
}

/**
 * Transformations to convert string fields to numeric values in Stord order API responses.
 */
export const STORD_ORDER_TRANSFORMATIONS: TransformationMapping = {
  // Sales order line fields (list responses: data is an array)
  '$.data[*].sales_order_lines[*].quantity': safeParseFloat,
  '$.data[*].sales_order_lines[*].sale_price': safeParseFloat,

  // Order line item fields within sales order lines
  '$.data[*].sales_order_lines[*].order_line_items[*].item_quantity': safeParseFloat,
  '$.data[*].sales_order_lines[*].order_line_items[*].primary_unit_quantity': safeParseFloat,

  // Single-order responses: data is one object
  '$.data.sales_order_lines[*].quantity': safeParseFloat,
  '$.data.sales_order_lines[*].sale_price': safeParseFloat,
  '$.data.sales_order_lines[*].order_line_items[*].item_quantity': safeParseFloat,
  '$.data.sales_order_lines[*].order_line_items[*].primary_unit_quantity': safeParseFloat,
}

/**
 * Combines multiple transformation mappings into a single mapping.
 * Later mappings will override earlier ones if they have the same key.
 */
export function combineTransformations(
  ...transformations: TransformationMapping[]
): TransformationMapping {
  return Object.assign({}, ...transformations)
}

/**
 * Generic function to apply transformations to webhook data using JSONPath.
 * This uses JSONPath to retrieve the data to be transformed, applies the transformation function
 * to the value at that path, and then replaces the original value with the transformed value.
 */
export function transformWebhookData(
  data: Record<string, unknown>,
  transformations: TransformationMapping,
): Record<string, unknown> {
  for (const [transformPath, transformation] of Object.entries(transformations)) {
    // Get the absolute paths to all the data that is to be transformed. This always returns an
    // array of paths, even if there is only one match.
    const absolutePaths = JSONPath<string[]>({
      path: transformPath,
      json: data,
      resultType: 'path',
    })

    for (let path of absolutePaths) {
      // Lodash doesn't accept the `$` prefix that JSONPath returns, so remove it
      path = path.replace(/^\$/, '')
      const value = _.get(data, path)
      if (_.isUndefined(value)) {
        // Skip if value doesn't exist (optional fields)
        continue
      }
      _.set(data, path, transformation(value))
    }
  }
  return data
}

/**
 * Applies transformations to convert string fields to numbers in Stord receipt confirmation webhook data.
 */
export function transformStordReceiptConfirmationData(
  data: Record<string, unknown>,
): Record<string, unknown> {
  return transformWebhookData(data, STORD_RECEIPT_CONFIRMATION_TRANSFORMATIONS)
}

/**
 * Applies transformations to convert string quantity/damages fields to numbers in the Stord get
 * receipt confirmations READ response.
 */
export function transformStordReceiptConfirmationsData(
  data: Record<string, unknown>,
): Record<string, unknown> {
  return transformWebhookData(data, STORD_RECEIPT_CONFIRMATIONS_READ_TRANSFORMATIONS)
}

/**
 * Applies transformations to convert string fields to numbers in Stord shipment confirmation webhook data.
 */
export function transformStordShipmentConfirmationData(
  data: Record<string, unknown>,
): Record<string, unknown> {
  return transformWebhookData(data, STORD_SHIPMENT_CONFIRMATION_TRANSFORMATIONS)
}

/**
 * Applies transformations to convert string quantity / package fields to numbers in the Stord
 * get shipment confirmations READ response.
 */
export function transformStordShipmentConfirmationsData(
  data: Record<string, unknown>,
): Record<string, unknown> {
  return transformWebhookData(data, STORD_SHIPMENT_CONFIRMATIONS_READ_TRANSFORMATIONS)
}

/**
 * Applies transformations to convert string quantity fields to numbers in the Stord
 * get delivery tracking v2 READ response.
 */
export function transformStordDeliveryTrackingData(
  data: Record<string, unknown>,
): Record<string, unknown> {
  return transformWebhookData(data, STORD_DELIVERY_TRACKING_READ_TRANSFORMATIONS)
}

/**
 * Applies transformations to convert string inventory quantity fields to numbers in Stord API responses.
 */
export function transformStordInventoryData(
  data: Record<string, unknown>,
): Record<string, unknown> {
  return transformWebhookData(data, STORD_INVENTORY_TRANSFORMATIONS)
}

/**
 * Applies transformations to convert string fields to numbers in Stord order API responses.
 */
export function transformStordOrderData(data: Record<string, unknown>): Record<string, unknown> {
  return transformWebhookData(data, STORD_ORDER_TRANSFORMATIONS)
}

// CANCEL ORDER -----------------------------------------------------------------------------------------

export const stordCancelSalesOrderActionStepInputSchema = yup.object({
  order_id: yup.string().required('Stord order ID is required'),
})

export type StordCancelSalesOrderActionStepInput = yup.InferType<
  typeof stordCancelSalesOrderActionStepInputSchema
>

// DOCUMENT TYPES -----------------------------------------------------------------------------------------

/**
 * Stord document type constants
 */
export const STORD_DOCUMENT_TYPES = [
  'Advanced Shipping Notice',
  'Bill of Lading',
  'Custom',
  'Damages',
  'Other',
  'Packing Slip',
  'Pick List',
  'Shipping Label',
]

export type StordDocumentType = (typeof STORD_DOCUMENT_TYPES)[number]
